import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  InputAdornment,
  TablePagination,
  alpha,
  MenuItem,
  Select,
  FormControl,
  Button,
} from '@mui/material';
import { Search, ArrowUpward, ArrowDownward, UnfoldMore, FilterAltOff } from '@mui/icons-material';
import { palette } from '../../theme';
import { EmptyState, PanelLoader } from '../ui/GlassPanel';

/**
 * Enterprise glass data table.
 * Search, column filters, sorting and pagination are built in so every screen
 * behaves identically.
 */
export default function DataTable({
  columns,
  rows = [],
  rowKey = (row) => row.id,
  searchKeys = [],
  searchPlaceholder = 'Search…',
  initialSearch = '',
  filters = [],
  pageSize = 8,
  loading = false,
  emptyTitle = 'No records found',
  emptyDescription = 'Adjust the filters or search terms to see more results.',
  emptyIcon,
  actions,
  toolbar,
  dense = false,
  resetKey = null,
}) {
  const [search, setSearch] = useState(initialSearch);
  const [filterValues, setFilterValues] = useState({});
  const [sort, setSort] = useState({ key: null, direction: 'asc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  useEffect(() => {
    if (initialSearch) {
      setSearch(initialSearch);
      setPage(0);
    }
  }, [initialSearch]);

  // Bumping the key (after a mutation) returns the table to a clean first
  // page so a freshly created record is immediately visible.
  useEffect(() => {
    if (resetKey === null) return;
    setPage(0);
    setSort({ key: null, direction: 'asc' });
    setFilterValues({});
    if (!initialSearch) setSearch('');
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    let result = rows.filter((row) => {
      const matchesTerm =
        !term ||
        searchKeys.some((key) => {
          const value = typeof key === 'function' ? key(row) : row[key];
          return String(value ?? '').toLowerCase().includes(term);
        });

      if (!matchesTerm) return false;

      return Object.entries(filterValues).every(([key, value]) => {
        if (!value || value === 'all') return true;
        const accessor = typeof key === 'function' ? key(row) : row[key];
        return String(accessor ?? '') === String(value);
      });
    });

    if (sort.key) {
      const column = columns.find((item) => item.key === sort.key);
      const accessor = column?.sortValue || column?.key;
      result = [...result].sort((a, b) => {
        const left = typeof accessor === 'function' ? accessor(a) : a[accessor];
        const right = typeof accessor === 'function' ? accessor(b) : b[accessor];
        if (left === right) return 0;
        if (left === null || left === undefined) return 1;
        if (right === null || right === undefined) return -1;
        const comparison =
          typeof left === 'number' && typeof right === 'number'
            ? left - right
            : String(left).localeCompare(String(right), undefined, { numeric: true });
        return sort.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [rows, search, filterValues, sort, columns, searchKeys]);

  const handleSort = (key) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const activeFilterCount = Object.values(filterValues).filter((value) => value && value !== 'all').length;

  return (
    <Box>
      {(searchKeys.length > 0 || filters.length > 0 || actions || toolbar) && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.25,
            p: 2,
            borderBottom: `1px solid ${alpha(palette.primary, 0.09)}`,
            background: alpha('#050816', 0.28),
          }}
        >
          {searchKeys.length > 0 && (
            <TextField
              size="small"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder={searchPlaceholder}
              sx={{ minWidth: { xs: 160, sm: 240 }, flex: { xs: '1 1 100%', sm: '0 1 260px' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {filters.map((filter) => (
            <FormControl key={filter.key} size="small" sx={{ minWidth: 150 }}>
              <Select
                value={filterValues[filter.key] || 'all'}
                onChange={(event) => {
                  setFilterValues((current) => ({ ...current, [filter.key]: event.target.value }));
                  setPage(0);
                }}
                displayEmpty
                sx={{ fontSize: '0.8rem' }}
              >
                <MenuItem value="all">{filter.label}: All</MenuItem>
                {filter.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}

          {activeFilterCount > 0 && (
            <Button
              size="small"
              startIcon={<FilterAltOff sx={{ fontSize: 16 }} />}
              onClick={() => {
                setFilterValues({});
                setSearch('');
                setPage(0);
              }}
              sx={{ color: 'text.secondary', fontSize: '0.76rem' }}
            >
              Reset
            </Button>
          )}

          <Box sx={{ flex: 1 }} />
          {toolbar}
          {actions}
        </Box>
      )}

      {loading ? (
        <PanelLoader label="Loading records" />
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size={dense ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || 'left'}
                    sx={{ width: column.width, whiteSpace: 'nowrap' }}
                    sortDirection={sort.key === column.key ? (sort.direction === 'asc' ? 'asc' : 'desc') : false}
                  >
                    {column.sortable === false ? (
                      column.label
                    ) : (
                      <Box
                        onClick={() => handleSort(column.key)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.4,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { color: palette.primary },
                        }}
                      >
                        {column.label}
                        {sort.key === column.key ? (
                          sort.direction === 'asc' ? (
                            <ArrowUpward sx={{ fontSize: 13 }} />
                          ) : (
                            <ArrowDownward sx={{ fontSize: 13 }} />
                          )
                        ) : (
                          <UnfoldMore sx={{ fontSize: 13, opacity: 0.35 }} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ border: 'none', p: 0 }}>
                    <EmptyState
                      icon={emptyIcon}
                      title={emptyTitle}
                      description={emptyDescription}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={rowKey(row)}
                      hover
                      sx={{
                        transition: 'background-color 180ms ease',
                        '&:hover': { background: alpha(palette.primary, 0.055) },
                        '& td': { borderColor: alpha(palette.primary, 0.07) },
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell key={column.key} align={column.align || 'left'}>
                          {column.render ? column.render(row) : row[column.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {filteredRows.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 8, 10, 25, 50]}
          sx={{
            borderTop: `1px solid ${alpha(palette.primary, 0.09)}`,
            color: 'text.secondary',
            '& .MuiTablePagination-toolbar': { minHeight: 48 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.76rem' },
          }}
        />
      )}
    </Box>
  );
}
